import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { CountryCode } from 'frontend-utils/utils/countries';
import Countries from 'frontend-utils/utils/countries';
import type { Day } from 'lumen/models';

interface AirportSearch {
  airport: string;

  search_fraction: number;
}

// Note: Countrysearch id examples:
// 0,2020-05-11,latest,BE
// 0,2020-05-20,2020-03-23,ES
// <type>,<day>,<extract date>,<country>
export default class Countrysearch extends Model {
  [Type] = 'countrysearch' as const;

  @belongsTo('day', { async: false, inverse: 'countrysearches' })
  declare day: Day;

  @attr() declare airportSearches: AirportSearch[];
  @attr('number') declare averageLos: number;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  @attr('string') declare country: CountryCode | string;
  // Used for the overview searches
  @attr('dayjs') declare endDay: DayjsTransformReturn;
  @attr('dayjs') declare extractDate: DayjsTransformReturn;
  @attr('boolean') declare isLatest: boolean;
  @attr('string') declare message: string;
  @attr('number') declare mostSearchedLos: number;
  // The fraction of total searches that are coming from this country
  @attr('number') declare searchFraction: number;
  @attr('number') declare type: number;

  @service declare intl: Services['intl'];
  private countries: Countries = new Countries(getOwner(this)!);

  // This is use to set the min percentage as 0.1% to avoid show 0.0% to indicate that there is at least some data for it included
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get airportSearchesWithSearchFractionRounding() {
    return this.airportSearches.map((airportSearch) => {
      airportSearch.search_fraction = Math.max(
        airportSearch.search_fraction,
        0.001,
      );
      return airportSearch;
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get averageLosFormatted() {
    return this.intl.formatNumber(this.averageLos, {
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get countryFormatted() {
    const countryUpperCase = this.country.toUpperCase();
    return this.countries.getName(countryUpperCase) || this.country;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchFractionAsPercentage() {
    return this.searchFraction * 100;
  }
}
